import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faShareAlt,
  faLocationDot,
  faStore,
  faUserTie,
  faTag,
  faClock,
  faCircleExclamation,
  faCircleCheck,
  faStar,
  faFlask,
  faCalendarCheck,
  faXmark,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";
import AILightComponent from "../../components/AILightComponent";

const HerKartServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    fetchLiveServices,
    submitFeedback,
    fetchFeedbackByRef,
    submitSampleRequest,
  } = useHerKart();

  const [liveServices, setLiveServices] = useState([]);
  const [loadingService, setLoadingService] = useState(true);

  const [message, setMessage] = useState(null);

  // Feedback state (level: "service")
  const [feedbackList, setFeedbackList] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [loadingFeedback, setLoadingFeedback] = useState(true);

  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Demo request state (reuses existing sample-request infra)
  // Demo request state (reuses existing sample-request infra)
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoNote, setDemoNote] = useState("");
  const [submittingDemo, setSubmittingDemo] = useState(false);

  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);
  const [enableAskAI, setEnableAskAI] = useState(false);

  const handleToggleSpeakTTS = () => {
    if (!service?.tts || !("speechSynthesis" in window)) return;

    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(service.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  const loadServices = async () => {
    setLoadingService(true);
    try {
      const response = await fetchLiveServices();
      if (response?.success) {
        setLiveServices(response.services || []);
      }
    } catch (e) {
      console.error("Service fetch error:", e);
      setMessage({ type: "error", text: "Could not load this service. Please try again." });
    } finally {
      setLoadingService(false);
    }
  };

  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const service = useMemo(
    () => liveServices.find((item) => String(item._id) === String(id)),
    [liveServices, id]
  );

  const loadFeedback = async () => {
    if (!service?._id) return;
    setLoadingFeedback(true);
    try {
      const response = await fetchFeedbackByRef("service", String(service._id));
      if (response?.success) {
        setFeedbackList(response.feedback || []);
        setAvgRating(response.avgRating || 0);
        setTotalRatings(response.totalRatings || 0);
      }
    } catch (e) {
      console.error("Service feedback fetch error:", e);
    } finally {
      setLoadingFeedback(false);
    }
  };

  useEffect(() => {
    loadFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?._id]);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!feedbackRating && !feedbackText.trim()) {
      setMessage({ type: "error", text: "Please give a rating or write feedback." });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const response = await submitFeedback({
        publisherCode: service.sellerCode,
        level: "service",
        refCode: String(service._id),
        refName: service.name,
        rating: feedbackRating,
        message: feedbackText.trim(),
      });

      if (response?.success) {
        setMessage({ type: "success", text: "Thank you for your feedback!" });
        setFeedbackRating(0);
        setFeedbackText("");
        loadFeedback();
      } else {
        setMessage({ type: "error", text: "Failed to submit feedback." });
      }
    } catch (e) {
      console.error("Feedback submit error:", e);
      setMessage({ type: "error", text: "Failed to submit feedback." });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const submitDemoRequest = async (e) => {
    e.preventDefault();
    if (!service) return;

    setSubmittingDemo(true);
    try {
      const response = await submitSampleRequest({
        publisherCode: service.sellerCode,
        type: "Demo",
        refCode: String(service._id),
        refName: service.name,
        note: demoNote.trim(),
      });

      if (response?.success) {
        setMessage({ type: "success", text: "Demo request sent! The provider will get in touch soon." });
        setShowDemoForm(false);
        setDemoNote("");
      } else {
        setMessage({ type: "error", text: "Failed to send demo request." });
      }
    } catch (e) {
      console.error("Demo request error:", e);
      setMessage({ type: "error", text: "Failed to send demo request." });
    } finally {
      setSubmittingDemo(false);
    }
  };

  if (loadingService) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading service...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#050816] text-white">
        <p className="text-slate-300">Service not found or no longer active.</p>
        <Link to="/herkart/services" className="rounded-xl bg-pink-600 px-5 py-2 font-semibold hover:bg-pink-500">
          Back to Services
        </Link>
      </div>
    );
  }

  const image = service.images?.[0] || service.image || "https://placehold.co/800x600/18181b/f9a8d4?text=HerKart+Services";

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/services"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <h1 className="text-lg font-bold">Service Details</h1>
          </div>

          <button
            onClick={() => navigator.share?.({ title: service.name, url: window.location.href })}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faShareAlt} />
          </button>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <img src={image} alt={service.name} className="h-80 w-full rounded-3xl object-cover" />

          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-fuchsia-900/40 px-3 py-1 text-xs font-semibold text-fuchsia-300">
              <FontAwesomeIcon icon={faTag} />
              {service.category || "Service"}
            </span>

            <div className="mt-3 flex items-center justify-between gap-3">
              <h2 className="text-2xl font-bold">{service.name}</h2>

              {service.tts && (
                <button
                  type="button"
                  onClick={handleToggleSpeakTTS}
                  title={isSpeakingTTS ? "Stop" : "Listen to service description"}
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all ${
                    isSpeakingTTS
                      ? "bg-pink-500 text-white"
                      : "bg-white/10 text-pink-300 hover:bg-white/20"
                  }`}
                >
                  <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
                </button>
              )}
            </div>

            <div className="mt-2 flex items-center gap-1 text-amber-400">
              <FontAwesomeIcon icon={faStar} />
              <span className="font-semibold">{avgRating || 0}</span>
              <span className="text-xs text-gray-400">({totalRatings || 0} ratings)</span>
            </div>

            <div className="mt-4 space-y-2 text-sm text-gray-300">
              {service.entrepreneur && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faUserTie} className="text-pink-400" />
                  {service.entrepreneur}
                </div>
              )}
              {service.store && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faStore} className="text-pink-400" />
                  {service.store} {service.storeCode ? `(${service.storeCode})` : ""}
                </div>
              )}
              {(service.locality || service.city) && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faLocationDot} className="text-pink-400" />
                  {service.locality}
                  {service.locality && service.city ? ", " : ""}
                  {service.city}
                </div>
              )}
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="text-pink-400" />
                {service.duration || "Duration on request"}
              </div>
            </div>

            {service.description && (
              <p className="mt-4 text-sm leading-relaxed text-slate-300">{service.description}</p>
            )}

            <div className="mt-4 rounded-2xl border border-purple-500/20 bg-white/5 p-4">
              <button
                type="button"
                onClick={() => setEnableAskAI((prev) => !prev)}
                className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
                  enableAskAI
                    ? "bg-purple-500 text-white"
                    : "bg-[#111827] text-purple-300 border border-purple-500/30"
                }`}
              >
                {enableAskAI ? "🙈 Hide GenAI" : "🤖 Ask AI About This Service"}
              </button>

              {enableAskAI && (
                <div className="mt-3 w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                  <AILightComponent
                    mode="herkart_service_customer_qna"
                    param1={"🗣️ Ask AI About This Service"}
                    param2={`
Service Details:
Name: ${service.name || "N/A"}
Category: ${service.category || "N/A"}
Description: ${service.description || "N/A"}
Entrepreneur/Provider: ${service.entrepreneur || "N/A"}
Price: ${service.price || "N/A"} rupees
Duration: ${service.duration || "N/A"}
Locality/City: ${service.locality || ""} ${service.city || ""}

You are a friendly voice shopping assistant. A customer is browsing this service on HerKart, a marketplace for women-led businesses, and wants to hear about it spoken aloud.

Using only the details above, speak naturally and warmly about the service — what it offers, who provides it, and anything a customer would want to know before booking.

Do NOT invent facts not present above. If details are missing, skip them gracefully instead of guessing.

STRICT OUTPUT RULES:
1. Output ONLY the spoken answer itself — nothing else.
2. Do NOT include any headings, labels, meta-commentary, introductions like "Here's what I found" or "Sure, here's the answer", disclaimers, or explanations about what you are doing.
3. Do NOT use markdown, bullet points, numbering, emojis, or asterisks — this will be read aloud or read as plain text exactly as written.
4. Write it exactly as you would say it out loud to the customer standing in front of you — natural spoken sentences only.
5. Keep it conversational and warm, like a helpful shop assistant, not a sales pitch.
`}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 rounded-2xl border border-fuchsia-500/10 bg-white/[0.03] p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Price</div>
              <div className="text-3xl font-extrabold text-white">
                ₹{service.price?.toLocaleString("en-IN")}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/herkart/services/${service._id}/book`)}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-600 px-5 py-3 font-semibold text-white shadow-lg transition hover:scale-[1.02]"
              >
                <FontAwesomeIcon icon={faCalendarCheck} />
                Book This Service
              </button>

              <button
                onClick={() => setShowDemoForm(true)}
                className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10"
              >
                <FontAwesomeIcon icon={faFlask} />
                Request Demo
              </button>
            </div>

            <p className="mt-2 text-xs text-slate-500">
              One provider per booking — this service is booked directly with {service.entrepreneur || "this provider"}.
            </p>
          </div>
        </div>

        {/* Feedback */}
        <div className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-6">
          <h3 className="mb-3 font-semibold text-cyan-200">Leave Feedback</h3>

          <form onSubmit={handleFeedbackSubmit}>
            <div className="mb-3 flex gap-1 text-2xl">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button"
                  key={star}
                  onClick={() => setFeedbackRating(star)}
                  onMouseEnter={() => setFeedbackHoverRating(star)}
                  onMouseLeave={() => setFeedbackHoverRating(0)}
                  className={(feedbackHoverRating || feedbackRating) >= star ? "text-amber-400" : "text-slate-600"}
                >
                  <FontAwesomeIcon icon={faStar} />
                </button>
              ))}
            </div>

            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value.slice(0, 250))}
              style={{height: "100px"}}
              placeholder="Share your experience with this service..."
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] p-3 text-sm text-white outline-none focus:border-pink-500"
            />
            <div className="mt-1 text-right text-xs text-slate-500">{feedbackText.length}/250</div>

            <button
              type="submit"
              disabled={submittingFeedback}
              className="mt-3 rounded-xl bg-pink-600 px-5 py-2 font-semibold hover:bg-pink-500 disabled:opacity-60"
            >
              {submittingFeedback ? "Submitting..." : "Submit Feedback"}
            </button>
          </form>

          <div className="mt-6 space-y-3">
            {loadingFeedback ? (
              <p className="text-sm text-slate-400">Loading reviews...</p>
            ) : feedbackList.length === 0 ? (
              <p className="text-sm text-slate-400">No reviews yet — be the first!</p>
            ) : (
              feedbackList.map((f) => (
                <div key={f._id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-white">{f.userName}</span>
                    {f.rating > 0 && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <FontAwesomeIcon icon={faStar} /> {f.rating}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-300">{f.message}</p>
                  {f.sellerReply && (
                    <p className="mt-2 rounded-lg bg-pink-500/10 p-2 text-xs text-pink-200">
                      <span className="font-semibold">Provider reply: </span>
                      {f.sellerReply}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showDemoForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b1120] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Request Demo — {service.name}</h3>
              <button onClick={() => setShowDemoForm(false)} className="text-slate-400 hover:text-white">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form onSubmit={submitDemoRequest}>
              <textarea
                value={demoNote}
                onChange={(e) => setDemoNote(e.target.value.slice(0, 250))}
                style={{height: "100px"}}
                placeholder="Preferred time, specific questions..."
                className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
              />
              <div className="mt-1 text-right text-xs text-slate-500">{demoNote.length}/250</div>

              <div className="mt-4 flex gap-3">
                <button
                  type="submit"
                  disabled={submittingDemo}
                  className="flex-1 rounded-xl bg-pink-600 px-4 py-2 font-semibold hover:bg-pink-500 disabled:opacity-60"
                >
                  {submittingDemo ? "Sending..." : "Send Request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDemoForm(false)}
                  className="flex-1 rounded-xl border border-white/10 px-4 py-2 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartServiceDetails;
